import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, CheckCircle, AlertCircle, ArrowLeft, Shield, Lock, FileText, Download } from 'lucide-react';
import {
  downloadInvoicePdf,
  fetchPublicInvoicePdf,
  fetchPublicInvoiceSummary,
  isInvoiceViewOnlyReference,
} from '../../lib/invoicePortal';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, CardElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../../lib/supabase';

interface PaymentData {
  id: string;
  amount: number;
  business_id: string;
  booking_id: string;
  product_sale_id?: string;
  booking_number: number;
  business_name: string;
  business_slug: string;
  customer_name: string;
  customer_email: string;
  /** CRM customer — used when save_card_on_file was requested on the invoice link. */
  customer_id: string | null;
  save_card_on_file: boolean;
  invoice_total: number;
  amount_paid: number;
  amount_due: number;
  is_deposit: boolean;
  stripe_connect_account_id: string;
  reference: string | null;
  surcharge_enabled: boolean;
  surcharge_percentage: number;
  tips_enabled: boolean;
  tips_go_to_resource: boolean;
  logo_url?: string | null;
  logo_invert?: boolean;
  brand_color?: string | null;
  business_phone?: string | null;
  business_email?: string | null;
  business_address?: string | null;
}

function formatPhone(value: string | null | undefined): string | null {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return value;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

function money(amount: number | null | undefined): string {
  return (amount ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function brandForeground(hex: string): { text: string; muted: string } {
  const raw = hex.replace('#', '').trim();
  const full = raw.length === 3 ? raw.split('').map((char) => char + char).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return { text: '#ffffff', muted: 'rgba(255,255,255,0.72)' };
  }
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const light = (r * 299 + g * 587 + b * 114) / 1000 > 170;
  return light
    ? { text: '#111827', muted: 'rgba(17,24,39,0.62)' }
    : { text: '#ffffff', muted: 'rgba(255,255,255,0.72)' };
}

async function loadPublicBusinessBrand(businessId: string): Promise<{
  logoUrl: string | null;
  logoInvert: boolean;
  brandColor: string;
  phone: string | null;
  email: string | null;
  address: string | null;
}> {
  const fallback = {
    logoUrl: null,
    logoInvert: false,
    brandColor: '#111111',
    phone: null,
    email: null,
    address: null,
  };
  try {
    const { data } = await supabase
      .from('businesses')
      .select('phone, email, address, booking_settings')
      .eq('id', businessId)
      .maybeSingle();
    if (!data) return fallback;
    const settings = (data.booking_settings || {}) as {
      logo_url?: string | null;
      logo_invert?: boolean;
      navbar_color?: string | null;
    };
    const email = typeof data.email === 'string' ? data.email.trim() : '';
    return {
      logoUrl: settings.logo_url || null,
      logoInvert: Boolean(settings.logo_invert),
      brandColor: settings.navbar_color || '#111111',
      phone: data.phone || null,
      email: email || null,
      address: typeof data.address === 'string' && data.address.trim() ? data.address.trim() : null,
    };
  } catch {
    return fallback;
  }
}

const tipOptions = [
  { label: 'No Tip', value: 0, percentage: 0 },
  { label: '10%', value: 10, percentage: 10 },
  { label: '15%', value: 15, percentage: 15 },
  { label: '18%', value: 18, percentage: 18 },
  { label: '20%', value: 20, percentage: 20 },
  { label: 'Custom', value: 'custom' as const, percentage: null }
];

// Inner form component that uses Stripe hooks
function PayInvoiceCardForm({
  paymentData,
  token,
  onSuccess,
  accentColor = '#111827',
}: {
  paymentData: PaymentData;
  token: string;
  onSuccess: () => void;
  accentColor?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardFunding, setCardFunding] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [detectingCardType, setDetectingCardType] = useState(false);
  const [selectedTipOption, setSelectedTipOption] = useState(tipOptions[0]);
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [billingComplete, setBillingComplete] = useState(false);
  const [billingDetails, setBillingDetails] = useState<any>(null);
  const [financingLoading, setFinancingLoading] = useState(false);

  const baseAmount = paymentData.amount_due;

  const tipAmount = (() => {
    if (selectedTipOption.value === 'custom') {
      return parseFloat(customTipAmount) || 0;
    }
    return (baseAmount * (selectedTipOption.percentage || 0)) / 100;
  })();

  const isCreditCard = cardFunding === 'credit';
  const applySurcharge = paymentData.surcharge_enabled && paymentData.surcharge_percentage > 0 && isCreditCard;
  const surchargeAmount = applySurcharge ? Math.round((baseAmount + tipAmount) * (paymentData.surcharge_percentage / 100) * 100) / 100 : 0;
  const totalAmount = Math.round((baseAmount + tipAmount + surchargeAmount) * 100) / 100;

  // Detect card funding type when card is fully entered
  useEffect(() => {
    if (!cardComplete || !paymentData.surcharge_enabled || paymentData.surcharge_percentage <= 0 || !stripe || !elements) {
      if (!cardComplete) setCardFunding(null);
      return;
    }

    const detectCardType = async () => {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;

      setDetectingCardType(true);
      try {
        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (pmError) {
          console.error('Card type detection error:', pmError);
          setCardFunding(null);
        } else if (paymentMethod) {
          console.log('Card type detected:', paymentMethod.card?.funding);
          setCardFunding(paymentMethod.card?.funding || null);
        }
      } catch (err) {
        console.error('Card type detection failed:', err);
        setCardFunding(null);
      } finally {
        setDetectingCardType(false);
      }
    };

    detectCardType();
  }, [cardComplete, paymentData.surcharge_enabled, paymentData.surcharge_percentage, stripe, elements]);

  const handleCardChange = (event: any) => {
    if (!event.complete) setCardFunding(null);
    setCardComplete(event.complete ?? false);
    setError(event.error ? event.error.message : null);
  };

  const handleFinancingClick = async () => {
    setFinancingLoading(true);
    setError(null);
    try {
      const financingTotal = Math.round((baseAmount + tipAmount) * 100);
      const currentUrl = window.location.href.split('?')[0];
      const successUrl = `${currentUrl}?financing_success=true`;
      const cancelUrl = currentUrl;

      const { data, error: fnError } = await supabase.functions.invoke('create-platform-payment', {
        body: {
          businessId: paymentData.business_id,
          bookingId: paymentData.booking_id || undefined,
          totalAmount: financingTotal,
          successUrl,
          cancelUrl,
          description: paymentData.booking_id
            ? `Payment for Invoice #${paymentData.booking_number}`
            : `Payment for Product Sale #${paymentData.booking_number}`,
          customerEmail: paymentData.customer_email,
          customerName: paymentData.customer_name,
          paymentMethodTypes: ['klarna', 'afterpay_clearpay', 'affirm'],
          paymentId: paymentData.id,
          metadata: {
            source: 'financing_payment_link',
            payment_id: paymentData.id,
            payment_token: token,
            booking_id: paymentData.booking_id || '',
            product_sale_id: paymentData.product_sale_id || '',
            base_amount: String(baseAmount),
            tip_amount: String(tipAmount),
            total_amount: String(baseAmount + tipAmount),
            surcharge_amount: '0',
            card_funding: 'financing',
          }
        }
      });

      if (fnError || !data?.checkoutUrl) {
        setError('Unable to load financing options. The business may not have financing enabled.');
        return;
      }

      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error('Financing error:', err);
      setError('Failed to load financing options. Please try again.');
    } finally {
      setFinancingLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setError('Payment system not loaded. Please refresh.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found. Please refresh.');
      return;
    }

    if (tipAmount < 0) {
      setError('Tip amount cannot be negative.');
      return;
    }

    if (!billingComplete || !billingDetails) {
      setError('Please complete your billing information.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    // If surcharge enabled but card type not yet detected, detect now
    let finalFunding = cardFunding;
    let finalSurcharge = surchargeAmount;
    let finalTotal = totalAmount;

    if (paymentData.surcharge_enabled && paymentData.surcharge_percentage > 0 && !cardFunding) {
      try {
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        if (paymentMethod?.card?.funding) {
          finalFunding = paymentMethod.card.funding;
          if (finalFunding === 'credit') {
            finalSurcharge = Math.round((baseAmount + tipAmount) * (paymentData.surcharge_percentage / 100) * 100) / 100;
            finalTotal = Math.round((baseAmount + tipAmount + finalSurcharge) * 100) / 100;
          }
        }
      } catch (err) {
        console.error('Card type detection at submit failed:', err);
      }
    }

    try {
      console.log('Creating Payment Intent for online payment:', {
        baseAmount,
        surcharge: finalSurcharge,
        tip: tipAmount,
        total: finalTotal,
      });

      const { data: intentData, error: intentError } = await supabase.functions.invoke(
        'create-payment-intent',
        {
          body: {
            businessId: paymentData.business_id,
            bookingId: paymentData.booking_id || undefined,
            amount: finalTotal,
            baseAmount: baseAmount,
            surchargeAmount: finalSurcharge,
            paymentId: paymentData.id,
            description: paymentData.booking_id
              ? `Payment for Invoice #${paymentData.booking_number}`
              : `Payment for Product Sale #${paymentData.booking_number}`,
            metadata: {
              source: 'online_payment_link',
              payment_id: paymentData.id,
              payment_token: token,
              booking_id: paymentData.booking_id || '',
              product_sale_id: paymentData.product_sale_id || '',
              base_amount: String(baseAmount),
              surcharge_amount: String(finalSurcharge),
              tip_amount: String(tipAmount),
              total_amount: String(finalTotal),
              card_funding: finalFunding || 'unknown',
            },
            ...(paymentData.save_card_on_file && paymentData.customer_id
              ? { saveCardForCustomerId: paymentData.customer_id }
              : {}),
          }
        }
      );

      if (intentError) {
        throw new Error(intentError.message || 'Failed to create payment. Please try again.');
      }

      const responseData = intentData?.data || intentData;
      if (!responseData?.clientSecret) {
        throw new Error('Payment setup failed. Please try again.');
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        responseData.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: billingDetails?.name || paymentData.customer_name || undefined,
              email: paymentData.customer_email || undefined,
              address: billingDetails?.address || undefined,
            }
          }
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment failed. Please check your card details.');
      }

      if (paymentIntent?.status === 'succeeded') {
        // Always ask the server to persist the card when staff enabled save on this link.
        // Do not gate on paymentData.save_card_* from the anon pay page: RLS/schema can hide those
        // fields even though the row and Payment Intent metadata are correct (service role in edge).
        if (paymentIntent.id && token) {
          try {
            const { data: saveRaw, error: saveErr } = await supabase.functions.invoke(
              'save-customer-card-after-online-payment',
              { body: { paymentToken: token, paymentIntentId: paymentIntent.id } },
            );
            const savePayload =
              saveRaw && typeof saveRaw === 'object' && 'data' in saveRaw && saveRaw.data
                ? saveRaw.data
                : saveRaw;
            if (saveErr) {
              console.error('Save card on file (online invoice):', saveErr.message);
            } else if (
              savePayload &&
              typeof savePayload === 'object' &&
              'error' in savePayload &&
              typeof (savePayload as { error?: string }).error === 'string'
            ) {
              console.error('Save card on file (online invoice):', (savePayload as { error: string }).error);
            }
          } catch (e) {
            console.error('Save card on file (online invoice):', e);
          }
        }

        // Update the existing pending payment record
        const { error: updateError } = await supabase
          .from('payments')
          .update({
            status: 'completed',
            amount: finalTotal,
            base_amount: baseAmount,
            surcharge_amount: finalSurcharge,
            tip_amount: tipAmount,
            stripe_payment_intent_id: paymentIntent.id,
            method: 'credit_card',
            channel: 'card_online',
            notes: finalSurcharge > 0
              ? `Online payment (includes $${finalSurcharge.toFixed(2)} processing fee)${tipAmount > 0 ? ` and $${tipAmount.toFixed(2)} tip` : ''}`
              : tipAmount > 0
              ? `Online payment (includes $${tipAmount.toFixed(2)} tip)`
              : 'Online payment via Stripe'
          })
          .eq('id', paymentData.id);

        if (updateError) {
          console.error('Failed to update payment record:', updateError);
        }

        // Distribute tip to resources via edge function (server-side, bypasses RLS)
        if (tipAmount > 0 && paymentData.booking_id) {
          try {
            await supabase.functions.invoke('distribute-tip', {
              body: {
                businessId: paymentData.business_id,
                bookingId: paymentData.booking_id,
                bookingNumber: paymentData.booking_number,
                tipAmount: tipAmount,
              },
            });
          } catch (tipErr) {
            console.error('Error distributing tip to resources:', tipErr);
          }
        }

        onSuccess();

        // Redirect to success page
        const isQueuePayment = paymentData.reference === 'queue-intake';
        const successUrl = isQueuePayment
          ? `${window.location.origin}/${paymentData.business_slug}/que/thank-you?booking=${paymentData.booking_id}`
          : `${window.location.origin}/pay-invoice/${token}/success`;
        window.location.href = successUrl;
      } else {
        throw new Error('Payment was not completed. Please try again.');
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#374151',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': { color: '#9CA3AF' },
      },
      invalid: { color: '#EF4444', iconColor: '#EF4444' },
    },
    hidePostalCode: true,
    disableLink: true,
  };

  const accentInk = brandForeground(accentColor);

  return (
    <>
      {/* Payment Amount - Read Only */}
      <div className="mb-6">
        <label className="mb-2 block text-[13px] font-medium text-gray-800">Payment Amount</label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
          <input
            type="text"
            value={paymentData.amount_due.toFixed(2)}
            readOnly
            className="w-full cursor-default rounded-2xl border border-gray-200 bg-[#f7f6f3] py-3.5 pl-9 pr-4 text-lg font-semibold tracking-tight text-gray-950"
          />
        </div>
        <p className="mt-2 text-[13px] text-gray-500">{paymentData.is_deposit ? 'Deposit amount due' : 'Full invoice amount due'}</p>
      </div>

      {/* Tip Section - only shown when tips are enabled by the business */}
      {paymentData.tips_enabled && (
        <div className="mb-6">
          <label className="mb-3 block text-[13px] font-medium text-gray-800">Add a Tip (Optional)</label>
          <div className="mb-3 grid grid-cols-3 gap-2">
            {tipOptions.map((option) => {
              const selected = selectedTipOption.value === option.value;
              return (
              <button
                key={String(option.value)}
                onClick={() => {
                  setSelectedTipOption(option);
                  if (option.value !== 'custom') setCustomTipAmount('');
                }}
                className={`rounded-xl border py-2.5 px-3 text-sm font-medium transition-colors ${
                  selected
                    ? ''
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                style={
                  selected
                    ? { backgroundColor: accentColor, borderColor: accentColor, color: accentInk.text }
                    : undefined
                }
              >
                {option.label}
              </button>
              );
            })}
          </div>
          {selectedTipOption.value === 'custom' && (
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                value={customTipAmount}
                onChange={(e) => {
                  setCustomTipAmount(e.target.value);
                  if (e.target.value && selectedTipOption.value !== 'custom') {
                    setSelectedTipOption(tipOptions[5]);
                  }
                }}
                min="0"
                step="0.01"
                className="w-full rounded-xl border border-gray-200 py-2.5 pl-8 pr-4 focus:border-gray-900 focus:ring-2 focus:ring-gray-900"
                placeholder="Enter custom tip amount"
              />
            </div>
          )}
        </div>
      )}

      {/* Billing Details - Stripe AddressElement */}
      <div className="mb-6">
        <label className="mb-2 block text-[13px] font-medium text-gray-800">Billing Information</label>
        <AddressElement
          options={{ mode: 'billing' }}
          onChange={(event) => {
            setBillingComplete(event.complete);
            if (event.complete) {
              setBillingDetails(event.value);
            }
          }}
        />
      </div>

      {/* Card Details */}
      <div className="mb-6">
        <label className="mb-2 block text-[13px] font-medium text-gray-800">Card Details</label>
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5 transition-all focus-within:border-gray-900 focus-within:ring-2 focus-within:ring-gray-900/10">
          <CardElement options={cardElementOptions} onChange={handleCardChange} />
        </div>
        {error && (
          <div className="mt-2 flex items-center text-sm text-red-600">
            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Surcharge Detection Feedback */}
      {cardComplete && paymentData.surcharge_enabled && paymentData.surcharge_percentage > 0 && (
        <div className="mb-4">
          {detectingCardType ? (
            <div className="flex items-center text-sm text-gray-400 py-1">
              <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin mr-2"></div>
              Detecting card type...
            </div>
          ) : applySurcharge ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              A {paymentData.surcharge_percentage}% card processing fee applies to credit card payments.
            </div>
          ) : cardFunding === 'debit' ? (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Debit card detected — no processing fee.
            </div>
          ) : null}
        </div>
      )}

      {/* Payment Summary */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-[#f7f6f3] p-4">
        <h4 className="mb-3 text-[13px] font-medium text-gray-800">Payment Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Base Amount</span>
            <span className="font-medium text-gray-900">${baseAmount.toFixed(2)}</span>
          </div>
          {surchargeAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-amber-700">Card Processing Fee ({paymentData.surcharge_percentage}%)</span>
              <span className="font-medium text-amber-700">${surchargeAmount.toFixed(2)}</span>
            </div>
          )}
          {tipAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Tip</span>
              <span className="font-medium text-gray-900">${tipAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
            <span className="text-gray-900 font-semibold">Total</span>
            <span className="font-semibold text-gray-900">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={isProcessing || !stripe || !cardComplete || !billingComplete}
        className="w-full rounded-2xl py-4 px-6 text-lg font-semibold transition-all duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: accentColor, color: accentInk.text }}
      >
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Processing...
          </div>
        ) : (
          `Pay ${money(totalAmount)}`
        )}
      </button>

      {/* Financing Option */}
      <div className="mt-4 relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-white text-gray-500">or</span>
        </div>
      </div>

      <button
        onClick={handleFinancingClick}
        disabled={financingLoading || isProcessing}
        className="mt-4 w-full rounded-2xl border border-gray-200 bg-white py-3.5 px-6 text-base font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {financingLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mr-2"></div>
            Loading options...
          </div>
        ) : (
          <span className="flex items-center justify-center gap-2">
            View Financing Options
            <span className="text-xs text-gray-400">(Klarna, Afterpay, Affirm)</span>
          </span>
        )}
      </button>

      {/* Security badges */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
          <div className="flex items-center">
            <Shield className="w-3.5 h-3.5 mr-1.5" />
            <span>Stripe Secure</span>
          </div>
          <div className="flex items-center">
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            <span>256-bit SSL</span>
          </div>
          <div className="flex items-center">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" />
            <span>All Cards Accepted</span>
          </div>
        </div>
      </div>
    </>
  );
}

type InvoicePortalTab = 'pay' | 'pdf';

interface ViewPortalData {
  bookingNumber: number | string;
  businessName: string;
  customerName: string;
  invoiceTotal: number;
  logoUrl?: string | null;
  logoInvert?: boolean;
  brandColor?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
}

function InvoicePdfPanel({
  token,
  bookingNumber,
}: {
  token: string;
  bookingNumber?: number | string;
}) {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState(`invoice-${bookingNumber ?? 'document'}.pdf`);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const isMobile =
    typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      const result = await fetchPublicInvoicePdf(token);
      if (cancelled) return;
      if (!result) {
        setLoadError('Unable to load invoice PDF. Please contact the business.');
        setLoading(false);
        return;
      }
      setPdfBlob(result.blob);
      setPreviewUrl(URL.createObjectURL(result.blob));
      setFilename(result.filename);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleDownload = async () => {
    if (!pdfBlob) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadInvoicePdf({ token, blob: pdfBlob, filename });
    } catch (err) {
      console.error('Invoice PDF download failed:', err);
      setDownloadError('Could not save the PDF. Try again or use the share option if shown.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600">Loading invoice PDF...</p>
        </div>
      </div>
    );
  }

  if (loadError || !previewUrl) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        {loadError || 'Invoice PDF not available'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
        <iframe
          title="Invoice PDF"
          src={previewUrl}
          className="w-full bg-white"
          style={{ height: 'min(70vh, 720px)' }}
        />
      </div>
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={handleDownload}
          disabled={!pdfBlob || downloading}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
        >
          <Download className="w-4 h-4" />
          {downloading
            ? 'Saving...'
            : isMobile
              ? 'Save PDF'
              : 'Download PDF'}
        </button>
        {isMobile && !downloadError && (
          <p className="text-xs text-gray-500 text-center max-w-xs px-2">
            Opens your device menu — choose <span className="font-medium">Save to Files</span> or{' '}
            <span className="font-medium">Downloads</span>.
          </p>
        )}
        {downloadError && (
          <p className="text-xs text-red-600 text-center max-w-xs px-2">{downloadError}</p>
        )}
      </div>
    </div>
  );
}

function getInitialTab(): InvoicePortalTab {
  const params = new URLSearchParams(window.location.search);
  return params.get('tab') === 'pdf' ? 'pdf' : 'pay';
}

export default function PayInvoice() {
  const { token } = useParams<{ token: string }>();
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [viewPortal, setViewPortal] = useState<ViewPortalData | null>(null);
  const [activeTab, setActiveTab] = useState<InvoicePortalTab>(getInitialTab);
  const [showPaymentTab, setShowPaymentTab] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [customPublicMessage, setCustomPublicMessage] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('financing_success') === 'true') {
      setPaymentSuccess(true);
      setLoading(false);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch payment details using token
  useEffect(() => {
    const fetchPaymentData = async () => {
      if (!token) {
        setError('Invalid payment link');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('payments')
          .select(`
            id,
            amount,
            payment_type,
            business_id,
            booking_id,
            invoice_id,
            product_sale_id,
            reference,
            save_card_on_file,
            save_card_customer_id,
            businesses!inner (
              id,
              name,
              slug,
              stripe_connect_account_id,
              stripe_connect_onboarding_completed,
              stripe_connect_charges_enabled,
              online_payments_enabled,
              credit_card_surcharge_enabled,
              credit_card_surcharge_percentage,
              tips_enabled,
              tips_go_to_resource
            )
          `)
          .eq('payment_token', token)
          .eq('status', 'pending')
          .single();

        if (fetchError || !data) {
          console.error('Payment not found:', fetchError);
          setDebugInfo({
            token,
            tokenLength: token?.length,
            error: fetchError,
            errorMessage: fetchError?.message,
            errorCode: fetchError?.code,
            errorDetails: fetchError?.details,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
            url: window.location.href
          });
          setError('Invoice link not found or has expired');
          setLoading(false);
          return;
        }

        try {
          const { data: msgData } = await supabase.rpc('get_invoice_public_message', {
            p_business_id: data.business_id,
          });
          if (msgData) {
            const enabled = msgData.enabled === true || msgData.enabled === 'true';
            const message = typeof msgData.message === 'string' ? msgData.message.trim() : '';
            setCustomPublicMessage(enabled && message ? message : null);
          }
        } catch (msgErr) {
          console.warn('Could not load invoice public message:', msgErr);
        }

        let customerInfo = { email: 'customer@example.com', name: 'Customer', bookingNumber: 'N/A' };
        let resolvedCustomerId: string | null = null;

        if (data.booking_id) {
          try {
            const { data: booking } = await supabase
              .from('bookings')
              .select(`
                booking_number,
                customer:customers!bookings_customer_id_fkey (
                  id,
                  full_name, first_name, last_name, email
                )
              `)
              .eq('id', data.booking_id)
              .single();

            if (booking) {
              const c = booking.customer as { id?: string; full_name?: string; first_name?: string; last_name?: string; email?: string } | null;
              if (c?.id) resolvedCustomerId = c.id;
              customerInfo = {
                email: c?.email || 'customer@example.com',
                name: c?.full_name ||
                      `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || 'Customer',
                bookingNumber: booking.booking_number || 'N/A'
              };
            }
          } catch {}
        }

        let bookingData = null;
        let productSaleData = null;
        let amountDue = 0;
        let totalPaid = 0;
        let customerName = 'Customer';
        let invoiceNumber: string | number = 'N/A';
        let resolvedInvoiceTotal = 0;

        if (data.booking_id) {
          const { data: fetchedBookingData, error: bookingError } = await supabase
            .from('bookings')
            .select(`
              id, booking_number, total_amount,
              customer:customers!bookings_customer_id_fkey(id, first_name, last_name),
              payments (amount, base_amount, status, payment_type)
            `)
            .eq('id', data.booking_id)
            .single();

          if (bookingError || !fetchedBookingData) {
            setError('Invoice details not found');
            setLoading(false);
            return;
          }

          bookingData = fetchedBookingData;

          // Always recalculate from booking payments to avoid stale amounts
          // and to exclude surcharges (CC processing fees) from balance calculation
          const validPayments = bookingData.payments?.filter((p: any) =>
            p.status === 'completed' || p.status === 'succeeded' || p.status === 'paid'
          ) || [];
          totalPaid = validPayments.reduce((sum: number, p: any) => sum + (p.base_amount || p.amount), 0);
          let bookingTotal = bookingData.total_amount;
          let fullBalance = Math.max(0, bookingTotal - totalPaid);

          // Prefer the new invoice system for money (token-scoped, service role).
          // Falls back to booking-derived totals for legacy bookings with no invoice.
          try {
            const invSummary = await fetchPublicInvoiceSummary(token);
            if (invSummary?.hasInvoice) {
              bookingTotal = invSummary.totalCents / 100;
              totalPaid = invSummary.amountPaidCents / 100;
              fullBalance = invSummary.amountDueCents / 100;
            }
          } catch {
            /* non-fatal: keep booking-derived totals */
          }
          resolvedInvoiceTotal = bookingTotal;

          // For deposits, use the stored amount if it's less than the full balance
          if (data.amount > 0 && data.payment_type === 'deposit' && data.amount < fullBalance) {
            amountDue = data.amount;
          } else {
            amountDue = fullBalance;
          }

          customerName = bookingData.customer
            ? `${bookingData.customer.first_name} ${bookingData.customer.last_name}`.trim()
            : 'Customer';
          invoiceNumber = bookingData.booking_number || 'N/A';
          if (!resolvedCustomerId && bookingData.customer && typeof (bookingData.customer as { id?: string }).id === 'string') {
            resolvedCustomerId = (bookingData.customer as { id: string }).id;
          }

        } else if (data.invoice_id) {
          // Standalone invoice (order / counter sale / rental / manual) — money,
          // number and customer come from the invoice itself, so the page
          // shows the real balance after wallet credit and earlier payments.
          try {
            const invSummary = await fetchPublicInvoiceSummary(token);
            if (!invSummary?.hasInvoice) { setError('Invoice details not found'); setLoading(false); return; }
            resolvedInvoiceTotal = invSummary.totalCents / 100;
            totalPaid = invSummary.amountPaidCents / 100;
            const fullBalance = invSummary.amountDueCents / 100;
            if (invSummary.status === 'paid' || fullBalance <= 0) {
              setError('This invoice has already been paid in full');
              setLoading(false);
              return;
            }
            amountDue = (data.amount > 0 && data.payment_type === 'deposit' && data.amount < fullBalance) ? data.amount : fullBalance;
            customerName = invSummary.customerName || 'Customer';
            invoiceNumber = invSummary.invoiceNumber || 'N/A';
            customerInfo = { ...customerInfo, name: invSummary.customerName || 'Customer', bookingNumber: invSummary.invoiceNumber || 'N/A' };
            if (data.product_sale_id && !resolvedCustomerId) {
              const { data: ps } = await supabase
                .from('product_sales')
                .select('customer_id')
                .eq('id', data.product_sale_id)
                .maybeSingle();
              if (ps?.customer_id) resolvedCustomerId = ps.customer_id as string;
            }
          } catch {
            setError('Invoice details not found'); setLoading(false); return;
          }
        } else if (data.product_sale_id) {
          const { data: fetchedProductSaleData, error: productSaleError } = await supabase
            .from('product_sales')
            .select(`
              id, sale_number, total_amount, payment_status,
              customer:customers!product_sales_customer_id_fkey(id, first_name, last_name, email)
            `)
            .eq('id', data.product_sale_id)
            .single();

          if (productSaleError || !fetchedProductSaleData) {
            setError('Invoice details not found');
            setLoading(false);
            return;
          }

          productSaleData = fetchedProductSaleData;
          amountDue = data.amount;
          resolvedInvoiceTotal = productSaleData.total_amount;

          if (productSaleData.payment_status === 'paid') {
            setError('This invoice has already been paid in full');
            setLoading(false);
            return;
          }

          customerName = productSaleData.customer
            ? `${productSaleData.customer.first_name} ${productSaleData.customer.last_name}`.trim()
            : 'Customer';
          invoiceNumber = productSaleData.sale_number || 'N/A';
          const pc = productSaleData.customer as { id?: string } | null;
          if (pc?.id) resolvedCustomerId = pc.id;
        } else {
          setError('Invoice details not found');
          setLoading(false);
          return;
        }

        const brand = await loadPublicBusinessBrand(data.business_id);

        const viewOnlyPortal = isInvoiceViewOnlyReference(data.reference);
        const canPayOnline =
          !viewOnlyPortal &&
          amountDue > 0 &&
          !!data.businesses.stripe_connect_account_id &&
          !!data.businesses.stripe_connect_onboarding_completed &&
          !!data.businesses.stripe_connect_charges_enabled &&
          data.businesses.online_payments_enabled !== false;

        setShowPaymentTab(canPayOnline);
        const params = new URLSearchParams(window.location.search);
        if (params.get('tab') === 'pdf' || viewOnlyPortal || !canPayOnline) {
          setActiveTab('pdf');
        } else {
          setActiveTab('pay');
        }

        if (!canPayOnline) {
          setViewPortal({
            bookingNumber: invoiceNumber,
            businessName: data.businesses.name,
            customerName,
            invoiceTotal: resolvedInvoiceTotal,
            logoUrl: brand.logoUrl,
            logoInvert: brand.logoInvert,
            brandColor: brand.brandColor,
            phone: brand.phone,
            email: brand.email,
            address: brand.address,
          });
          setLoading(false);
          return;
        }

        setPaymentData({
          id: data.id,
          amount: data.amount,
          business_id: data.business_id,
          booking_id: data.booking_id,
          product_sale_id: data.product_sale_id,
          booking_number: (typeof invoiceNumber === 'number' ? invoiceNumber : (invoiceNumber !== 'N/A' ? parseInt(invoiceNumber.toString()) : (parseInt(customerInfo.bookingNumber) || 0))),
          business_name: data.businesses.name,
          business_slug: data.businesses.slug,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_id: (data as { save_card_customer_id?: string | null }).save_card_customer_id || resolvedCustomerId,
          save_card_on_file: Boolean(data.save_card_on_file),
          invoice_total: resolvedInvoiceTotal || amountDue,
          amount_paid: totalPaid,
          amount_due: amountDue,
          is_deposit: data.payment_type === 'deposit',
          stripe_connect_account_id: data.businesses.stripe_connect_account_id,
          reference: data.reference,
          surcharge_enabled: data.businesses.credit_card_surcharge_enabled ?? false,
          surcharge_percentage: data.businesses.credit_card_surcharge_percentage ?? 0,
          tips_enabled: data.businesses.tips_enabled ?? false,
          tips_go_to_resource: data.businesses.tips_go_to_resource ?? false,
          logo_url: brand.logoUrl,
          logo_invert: brand.logoInvert,
          brand_color: brand.brandColor,
          business_phone: brand.phone,
          business_email: brand.email,
          business_address: brand.address,
        });

      } catch (err) {
        console.error('Error fetching payment data:', err);
        setDebugInfo({
          token,
          error: err,
          errorMessage: err instanceof Error ? err.message : String(err),
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        });
        setError('Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [token]);

  // Initialize Stripe.js only when the Pay tab is available
  useEffect(() => {
    if (!paymentData || !showPaymentTab) return;

    const initStripe = async () => {
      setStripeLoading(true);
      try {
        const platformKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        const { data: keyData, error: keyError } = await supabase.functions.invoke(
          'get-stripe-connect-publishable-key',
          { body: { businessId: paymentData.business_id, platformPublishableKey: platformKey } }
        );

        if (keyError || !keyData?.publishableKey || !keyData?.accountId) {
          setStripeError('Unable to initialize payment system. Please try again later.');
          setStripeLoading(false);
          return;
        }

        const stripe = await loadStripe(keyData.publishableKey, {
          stripeAccount: keyData.accountId
        } as any);
        setStripePromise(Promise.resolve(stripe));
      } catch (err: any) {
        console.error('Stripe initialization error:', err);
        setStripeError(err.message || 'Failed to initialize payment system');
      } finally {
        setStripeLoading(false);
      }
    };

    initStripe();
  }, [paymentData, showPaymentTab]);

  const pageFrame = 'min-h-screen bg-[#f3f0ea] px-4 py-8 sm:py-14';
  const pageColumn = 'mx-auto w-full max-w-[560px]';

  if (loading) {
    return (
      <div className={`${pageFrame} flex items-center justify-center`}>
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-gray-900"></div>
          <p className="font-medium text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${pageFrame} flex items-center justify-center`}>
        <div className="w-full max-w-lg rounded-[28px] bg-white p-8 shadow-[0_24px_70px_-36px_rgba(40,30,20,0.45)]">
          <div className="text-center mb-6">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Payment Not Available</h1>
            <p className="text-gray-600 mb-4">{error}</p>
          </div>
          {debugInfo && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Debug Information</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Token:</strong> <span className="font-mono bg-white px-2 py-1 rounded">{debugInfo.token || 'null'}</span></div>
                <div><strong>Timestamp:</strong> {debugInfo.timestamp}</div>
                {debugInfo.error && (
                  <div className="mt-3 p-3 bg-red-50 rounded border">
                    <div><strong>Error:</strong> {debugInfo.errorMessage || 'No message'}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="text-center">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    const successName = paymentData?.business_name;
    return (
      <div className={`${pageFrame} flex items-center justify-center`}>
        <div className="w-full max-w-md rounded-[28px] bg-white p-8 text-center shadow-[0_24px_70px_-36px_rgba(40,30,20,0.45)]">
          <CheckCircle className="mx-auto mb-4 h-16 w-16 text-gray-900" />
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">Payment Successful</h1>
          <p className="text-gray-600">
            {successName ? `${successName} received your payment. ` : ''}
            You should receive a confirmation email shortly.
          </p>
        </div>
      </div>
    );
  }

  const elementsOptions: StripeElementsOptions = {
    appearance: { theme: 'stripe' as const },
    loader: 'auto',
  };

  const displayNumber = paymentData?.booking_number ?? viewPortal?.bookingNumber;
  const displayBusiness = paymentData?.business_name ?? viewPortal?.businessName ?? '';
  const rawCustomer = (paymentData?.customer_name ?? viewPortal?.customerName ?? '').trim();
  const displayCustomer = rawCustomer.toLowerCase() === 'customer' ? '' : rawCustomer;
  const logoUrl = paymentData?.logo_url ?? viewPortal?.logoUrl;
  const logoInvert = paymentData?.logo_invert ?? viewPortal?.logoInvert ?? false;
  const brandColor = paymentData?.brand_color || viewPortal?.brandColor || '#111111';
  const businessPhone = paymentData?.business_phone ?? viewPortal?.phone;
  const businessEmail = paymentData?.business_email ?? viewPortal?.email;
  const businessAddress = paymentData?.business_address ?? viewPortal?.address;
  const phoneLabel = formatPhone(businessPhone);
  const pdfOnlyLayout = viewPortal && !paymentData && token;
  const brandInk = brandForeground(brandColor);
  const initials = displayBusiness
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

  const brandBanner = (
    <div className="px-6 pb-8 pt-9 text-center sm:px-8" style={{ backgroundColor: brandColor, color: brandInk.text }}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="mx-auto h-16 max-w-[220px] rounded-2xl object-contain"
          style={{ filter: logoInvert ? 'brightness(0) invert(1)' : undefined }}
        />
      ) : (
        <div
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-lg font-semibold"
          style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: brandInk.text }}
        >
          {initials || '•'}
        </div>
      )}
      <h1 className="mt-4 text-[22px] font-semibold tracking-tight">{displayBusiness}</h1>
      <div className="mx-auto mt-3 max-w-sm space-y-1 text-[13px] leading-relaxed" style={{ color: brandInk.muted }}>
        {phoneLabel ? (
          <a href={`tel:${String(businessPhone).replace(/[^\d+]/g, '')}`} className="m-0 block hover:underline">
            {phoneLabel}
          </a>
        ) : null}
        {businessEmail ? (
          <a href={`mailto:${businessEmail}`} className="m-0 block hover:underline">
            {businessEmail}
          </a>
        ) : null}
        {businessAddress ? <p className="m-0">{businessAddress}</p> : null}
      </div>
      <p className="mt-4 text-[13px]" style={{ color: brandInk.muted }}>
        Invoice #{displayNumber}
        {displayCustomer ? ` · ${displayCustomer}` : ''}
      </p>
    </div>
  );

  if (pdfOnlyLayout) {
    return (
      <div className={pageFrame}>
        <div className={pageColumn}>
          <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_24px_70px_-36px_rgba(40,30,20,0.45)]">
            {brandBanner}
            <div className="px-6 py-7 text-center sm:px-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Invoice total</p>
              <p className="mt-1 text-[40px] font-semibold tracking-tight text-gray-950">{money(viewPortal.invoiceTotal)}</p>
              {displayCustomer ? (
                <p className="mt-2 text-[13px] text-gray-500">{displayCustomer}</p>
              ) : null}
            </div>
            {customPublicMessage ? (
              <p className="mx-6 mb-2 whitespace-pre-line text-center text-[13px] leading-relaxed text-gray-600 sm:mx-8">
                {customPublicMessage}
              </p>
            ) : null}
            <div className="border-t border-gray-100 px-6 py-6 sm:px-8">
              <InvoicePdfPanel token={token} bookingNumber={displayNumber} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const summaryRows: Array<{ label: string; value: string; emphasis?: boolean }> = [];
  if (paymentData) {
    summaryRows.push({ label: 'Invoice total', value: money(paymentData.invoice_total) });
    if (paymentData.is_deposit) {
      if (paymentData.amount_paid > 0) {
        summaryRows.push({ label: 'Previously paid', value: money(paymentData.amount_paid) });
      }
      summaryRows.push({ label: 'Deposit due', value: money(paymentData.amount_due), emphasis: true });
      summaryRows.push({
        label: 'Remaining after deposit',
        value: money(paymentData.invoice_total - paymentData.amount_paid - paymentData.amount_due),
      });
    } else {
      summaryRows.push({ label: 'Amount paid', value: money(paymentData.amount_paid) });
      summaryRows.push({ label: 'Amount due', value: money(paymentData.amount_due), emphasis: true });
    }
  }

  return (
    <div className={pageFrame}>
      <div className={pageColumn}>
        <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_24px_70px_-36px_rgba(40,30,20,0.45)]">
          {brandBanner}
          <div className="px-6 pb-2 pt-7 text-center sm:px-8">
            {paymentData?.is_deposit ? (
              <span className="mb-3 inline-block rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
                Deposit requested
              </span>
            ) : null}
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              {paymentData?.is_deposit ? 'Deposit due' : 'Amount due'}
            </p>
            <p className="mt-1 text-[42px] font-semibold leading-none tracking-tight text-gray-950">
              {money(paymentData?.amount_due)}
            </p>
          </div>

          {summaryRows.length > 0 ? (
            <div className="mx-6 mt-5 divide-y divide-gray-200/80 rounded-2xl bg-[#f7f6f3] px-4 sm:mx-8">
              {summaryRows.map((row) => (
                <div key={row.label} className="flex items-center justify-between py-2.5 text-[13px]">
                  <span className="text-gray-500">{row.label}</span>
                  <span className={`tabular-nums ${row.emphasis ? 'font-semibold text-gray-950' : 'font-medium text-gray-800'}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {customPublicMessage ? (
            <p className="mx-6 mt-5 whitespace-pre-line text-center text-[13px] leading-relaxed text-gray-600 sm:mx-8">
              {customPublicMessage}
            </p>
          ) : null}

          {showPaymentTab && token ? (
            <div className="mx-6 mt-6 grid grid-cols-2 gap-1 rounded-2xl bg-[#f3f0ea] p-1 sm:mx-8">
              <button
                type="button"
                onClick={() => setActiveTab('pay')}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === 'pay' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <CreditCard className="h-4 w-4" />
                Pay
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('pdf')}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === 'pdf' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <FileText className="h-4 w-4" />
                PDF
              </button>
            </div>
          ) : null}

          <div className="px-6 py-6 sm:px-8">
            {activeTab === 'pdf' && token ? (
              <InvoicePdfPanel token={token} bookingNumber={displayNumber} />
            ) : showPaymentTab && paymentData ? (
              <>
                <h2 className="mb-5 text-[18px] font-semibold tracking-tight text-gray-950">
                  {paymentData.is_deposit ? 'Pay deposit' : 'Make a payment'}
                </h2>
                {stripeLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-gray-900"></div>
                      <p className="text-sm text-gray-600">Loading payment form...</p>
                    </div>
                  </div>
                ) : stripeError ? (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                    <div className="flex items-start text-red-700">
                      <AlertCircle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0" />
                      <span className="text-sm">{stripeError}</span>
                    </div>
                  </div>
                ) : stripePromise && token ? (
                  <Elements stripe={stripePromise} options={elementsOptions}>
                    <PayInvoiceCardForm
                      paymentData={paymentData}
                      token={token}
                      accentColor={brandColor}
                      onSuccess={() => setPaymentSuccess(true)}
                    />
                  </Elements>
                ) : null}
              </>
            ) : token ? (
              <InvoicePdfPanel token={token} bookingNumber={displayNumber} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
